import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import { usePortfolioIdReportingQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioIdReportingQuery";
import { ClientPortfolioPaymentFrequencyEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import {
	BasicInfo,
	hasBasicInfo,
} from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { ValueChartSection } from "web_myplann/Portfolio/Components/PortfolioSections/ValueChartSection";
import { InvestmentTool } from "web_myplann/Portfolio/Components/PortfolioTools/InvestmentTool/InvestmentTool";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { calculateGraphData } from "web_myplann/Portfolio/Helpers/calculateGraphData";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import { getPaidAllLogic } from "web_myplann/Portfolio/Helpers/getPaidAllLogic";
import { normalizeTools } from "web_myplann/Portfolio/Helpers/normalizeTools";
import { getPortfolioFrequencyValue } from "web_myplann/Portfolio/Helpers/portfolioDetailFormatters";
import { DataCardPreset } from "web_myplann/Shared/Components/DataCard";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail;

export const Investment: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();
	const tools = normalizeTools(baseData.tools);

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);
	const { data: reporting, isLoading: isReportingLoading } =
		usePortfolioIdReportingQuery(baseData.id);

	if (isDocumentsLoading || isReportingLoading) {
		return <DetailSectionLoader />;
	}

	const graphData = calculateGraphData(
		reporting?.reportingData,
		reporting?.summary ?? undefined,
	);

	const { paidAllValue, shouldShowPaidAll } = getPaidAllLogic({ baseData });

	const paidAll = getPortfolioFrequencyValue(
		paidAllValue,
		t(Resources.PortfolioDetail.SharedLabels.Currency[baseData.currency]),
		ClientPortfolioPaymentFrequencyEnum.Monthly,
		t,
		0,
	);

	const basicInfoItems: DataRowCardItem[] = [
		{
			label: t(PageResources.SharedLabels.name),
			value: baseData.clientName,
		},
		{
			label: t(PageResources.SharedLabels.contractNumber),
			value: baseData.contractNumber,
		},
		{
			label: t(PageResources.SharedLabels.producent),
			value: baseData.producer,
		},
		{
			label: t(PageResources.SharedLabels.product),
			value: baseData.product,
		},
		{
			label: t(PageResources.SharedLabels.paidAll),
			value: shouldShowPaidAll ? paidAll : null,
		},
	];

	return (
		<Stack>
			<ValueChartSection
				baseData={baseData}
				summary={reporting?.summary}
				interpolatedRanges={reporting?.interpolatedRanges}
				graphData={graphData}
				currencySign={currencySign}
			/>

			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={
					hasBasicInfo(baseData) ? <BasicInfo data={baseData} /> : null
				}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related, tools)
						.hasAny && tools.length === 0
				}
				items={basicInfoItems}
			/>

			<InvestmentTool
				tools={tools}
				producerInstruments={baseData.producerInstruments}
				toolsSummary={reporting?.toolsSummary}
				portfolioId={baseData.id}
				title={t(PageResources.Investment.InvestmentTool.title)}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
			/>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</Stack>
	);
};
